import {
    AuditLogChangeType,
    AuditLogDiff,
    HistoryDetailsItem,
} from "@common/types/auditLog";
import {
    COA_HIDDEN_FIELDS,
    HistoryField,
    toInternalHistorySourceType,
} from "@common/constants/history-source-types";
import { APIProcessName } from "@common/constants/process-names";
import { getHistoryFieldLabel } from "../../utils/historyDetailsHelpers";
import {
    HistoryAccountDetailsRow,
    HistoryAccountLine,
} from "../views/historyAccountDetailsViewConfig";
import { HistoryAccountTableKind } from "../views/historyAccountDetailsView";
import { formatDiffValues, IdentifierFormatter } from "./coaActivityValues";
import {
    buildIdentityLine,
    buildRow,
    groupLogsBy,
    LogGroup,
} from "./historyAccountLines";
import { buildMergeRows } from "./historyAccountMergeRows";

const GROUP_DELETE_SOURCE_TYPES: readonly string[] = [
    APIProcessName.COA_ENTITY_ACCOUNT_GROUP_DELETE,
    APIProcessName.COA_CHART_EDITOR_DELETE,
];

const HEADER_LABEL_OVERRIDES: Partial<Record<string, string>> = {
    [HistoryField.OPENING_BALANCE]: "balance",
};

export function getAccountFieldLabel(field: string): string | undefined {
    if (COA_HIDDEN_FIELDS.includes(field)) {
        return undefined;
    }
    return getHistoryFieldLabel(field);
}

export interface HistoryAccountTable {
    kind: HistoryAccountTableKind;
    rows: HistoryAccountDetailsRow[];
    changedFieldLabel: string | null;
}

interface BuildHistoryAccountTableParams {
    logs: HistoryDetailsItem[];
    sourceType: string;
    selectedFields: string[];
    formatIdentifier: IdentifierFormatter;
}

function getDisplayableDiffs(
    log: HistoryDetailsItem,
    selectedFields: string[],
): AuditLogDiff[] {
    const displayableDiffs = log.diff.filter(
        (diff) => !COA_HIDDEN_FIELDS.includes(diff.field),
    );
    return selectedFields.length > 0
        ? displayableDiffs.filter((diff) => selectedFields.includes(diff.field))
        : displayableDiffs;
}

interface BuildChangeLinesParams {
    log: HistoryDetailsItem;
    selectedFields: string[];
    formatIdentifier: IdentifierFormatter;
}

function buildChangeLines({
    log,
    selectedFields,
    formatIdentifier,
}: BuildChangeLinesParams): HistoryAccountLine[] {
    const identityLine = buildIdentityLine({ log, formatIdentifier });
    if (log.changeType !== AuditLogChangeType.UPDATE) {
        return [identityLine];
    }
    return getDisplayableDiffs(log, selectedFields).map((diff) => ({
        ...identityLine,
        id: `${log.id}-${diff.field}`,
        changedField: diff.field,
        ...formatDiffValues(log, diff),
    }));
}

interface BuildRowsParams {
    logs: HistoryDetailsItem[];
    selectedFields: string[];
    formatIdentifier: IdentifierFormatter;
}

function buildIdentityRows({
    logs,
    formatIdentifier,
}: BuildRowsParams): HistoryAccountDetailsRow[] {
    return logs.map((log) =>
        buildRow({
            id: log.id,
            log,
            lines: [buildIdentityLine({ log, formatIdentifier })],
        }),
    );
}

function buildChangeRows({
    logs,
    ...options
}: BuildRowsParams): HistoryAccountDetailsRow[] {
    return logs.flatMap((log) =>
        buildChangeLines({ log, ...options }).map((line) =>
            buildRow({ id: line.id, log, lines: [line] }),
        ),
    );
}

const isUpdateLog = (log: HistoryDetailsItem): boolean =>
    log.changeType === AuditLogChangeType.UPDATE;

interface BuildGroupDeleteRowParams extends Omit<BuildRowsParams, "logs"> {
    actionLogs: LogGroup;
}

function buildGroupDeleteRow({
    actionLogs,
    ...options
}: BuildGroupDeleteRowParams): HistoryAccountDetailsRow {
    const deletedLines = actionLogs
        .filter((log) => !isUpdateLog(log))
        .map((log) =>
            buildIdentityLine({
                log,
                formatIdentifier: options.formatIdentifier,
                label: "Deleted",
            }),
        );
    const movedLines = actionLogs
        .filter(isUpdateLog)
        .flatMap((log) => buildChangeLines({ log, ...options }))
        .map((line) => ({ ...line, label: "Moved to parent" }));
    const [firstLog] = actionLogs;
    return buildRow({
        id: firstLog.id,
        log: firstLog,
        lines: [...deletedLines, ...movedLines],
    });
}

function buildGroupDeleteRows({
    logs,
    ...options
}: BuildRowsParams): HistoryAccountDetailsRow[] {
    return groupLogsBy(logs, (log) => log.sourceId ?? log.id).map(
        (actionLogs) => buildGroupDeleteRow({ actionLogs, ...options }),
    );
}

function resolveKind({
    logs,
    sourceType,
    selectedFields,
}: Omit<
    BuildHistoryAccountTableParams,
    "formatIdentifier"
>): HistoryAccountTableKind {
    const internalSourceType = toInternalHistorySourceType(sourceType);
    if (internalSourceType === APIProcessName.COA_ACCOUNT_MERGE) {
        return HistoryAccountTableKind.MERGE;
    }
    const hasFieldChange = logs.some(
        (log) =>
            log.changeType === AuditLogChangeType.UPDATE &&
            getDisplayableDiffs(log, selectedFields).some(
                (diff) => diff.field !== HistoryField.ARCHIVED,
            ),
    );
    if (!hasFieldChange) {
        return HistoryAccountTableKind.IDENTITY;
    }
    return GROUP_DELETE_SOURCE_TYPES.includes(internalSourceType)
        ? HistoryAccountTableKind.GROUP_DELETE
        : HistoryAccountTableKind.CHANGE;
}

function resolveChangedFieldLabel(
    rows: HistoryAccountDetailsRow[],
): string | null {
    const changedFields = new Set(
        rows
            .flatMap((row) => row.lines)
            .map((line) => line.changedField)
            .filter((field): field is string => field !== null),
    );
    if (changedFields.size !== 1) {
        return null;
    }
    const [field] = changedFields;
    return HEADER_LABEL_OVERRIDES[field] ?? getAccountFieldLabel(field) ?? null;
}

export function buildHistoryAccountTable({
    logs,
    ...options
}: BuildHistoryAccountTableParams): HistoryAccountTable {
    const params = {
        ...options,
        logs: logs.filter(
            (log) =>
                log.changeType !== AuditLogChangeType.UPDATE ||
                getDisplayableDiffs(log, options.selectedFields).length > 0,
        ),
    };
    const kind = resolveKind(params);
    const rows = {
        [HistoryAccountTableKind.IDENTITY]: () => buildIdentityRows(params),
        [HistoryAccountTableKind.CHANGE]: () => buildChangeRows(params),
        [HistoryAccountTableKind.MERGE]: () => buildMergeRows(params),
        [HistoryAccountTableKind.GROUP_DELETE]: () =>
            buildGroupDeleteRows(params),
    }[kind]();
    return { kind, rows, changedFieldLabel: resolveChangedFieldLabel(rows) };
}
